import { HttpClient } from "@angular/common/http";
import { Injectable, computed, inject, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { AppConfig } from "../../app.config";
import { ProjectService } from "../../shared/services/project.service";
import {
  AgentActivity,
  AgentDetail,
  Artifact,
  ChatMsg,
  ChatResponse,
  Conversation,
  ConversationDetail,
  SessionResponse,
  StreamStatus,
  ToolStep,
  Usage,
} from "../xwai.types";

/**
 * The XWAI v2 chat service: streaming state and transport (SSE over `fetch`).
 */

@Injectable({ providedIn: "root" })
export class XwaiV2StreamService {
  private http = inject(HttpClient);
  private projectService = inject(ProjectService);
  private notification = inject(NzNotificationService);

  /** Job the session is scoped to, so the backend can attach job context to answers. */
  private readonly _jobId = signal<string | null>(null);
  readonly jobId = this._jobId.asReadonly();

  /** Set (or clear, with `null`) the job this session should be scoped to. */
  setJobId(jobId: string | null): void {
    this._jobId.set(jobId);
  }

  // --- finalized conversation history ---
  private readonly _threadId = signal<string | null>(null);
  private readonly _messages = signal<ChatMsg[]>([
    { role: "xwai", content: { answer: "Hi! How can I help you today?" } },
  ]);
  private readonly _initializing = signal(false);
  private readonly _status = signal<StreamStatus>("idle");

  // --- live state for the in-flight streaming turn ---
  private readonly _streamAnswer = signal("");
  private readonly _streamAgents = signal<AgentActivity[]>([]);
  private readonly _streamSteps = signal<ToolStep[]>([]); // orchestrator-level tool calls
  private readonly _streamArtifacts = signal<Artifact[] | null>(null);
  private readonly _streamUsage = signal<Usage | null>(null);
  private readonly _streamError = signal<string | null>(null);

  private ctrl?: AbortController;

  /**
   * `agent_tool_result`s that arrived before their `agent_step` (shouldn't
   * happen per the contract, but we buffer to be safe). Keyed by
   * `${callId}-${toolCallId}`; drained when the matching step appears.
   */
  private pendingResults = new Map<string, string>();

  readonly threadId = this._threadId.asReadonly();
  readonly messages = this._messages.asReadonly();
  readonly initializing = this._initializing.asReadonly();
  readonly status = this._status.asReadonly();
  readonly streamAnswer = this._streamAnswer.asReadonly();
  readonly streamAgents = this._streamAgents.asReadonly();
  readonly streamSteps = this._streamSteps.asReadonly();
  readonly streamError = this._streamError.asReadonly();

  isLoading = signal<boolean>(false)

  /** Sent user messages, oldest first — for shell-style up/down recall in the input. */
  private messageHistory: string[] = [];

  /** Sent user messages, oldest first (read-only). */
  get history(): readonly string[] {
    return this.messageHistory;
  }

  /** True while a response is streaming. */
  readonly sending = computed(() => this._status() === "streaming");

  private baseUrl: string = `${AppConfig.settings.apiUrl_v2}/xwai`;

  constructor() {
    this.fetchConversations().then((res) => {
      this.conversations.set(res);
    });
  }

  readonly loadingConversations = signal<boolean>(false);
  conversations = signal<Conversation[]>([]);
  private async fetchConversations(): Promise<Conversation[]> {
    this.loadingConversations.set(true);
    try {
      const response = await firstValueFrom(
        this.http.get(`${this.baseUrl}/conversations`, {
          headers: this.projectService.getAuthorizationHeader(),
        }),
      );
      return response as Conversation[];
    } catch (error) {
      console.error("Error fetching conversations:", error);
      this.notification.error("Error", "Failed to fetch conversations.");
      return [];
    } finally {
      this.loadingConversations.set(false);
    }
  }

  private async getConversation(id: string): Promise<ConversationDetail> {
    try {
      const response = await firstValueFrom(
        this.http.get(`${this.baseUrl}/conversations/${id}`, {
          headers: this.projectService.getAuthorizationHeader(),
        }),
      );
      return response as ConversationDetail;
    } catch (error: any) {
      console.error("Error saving conversation:", error);
      const detail =
        typeof error?.error === "string" ? "" : error?.error?.detail;
      this.notification.error("Error", "Failed to get conversation. " + detail);
      throw error;
    }
  }

  public activeConversation = signal<Conversation | null>(null)

  async deleteConverstaion(id: string) {
    try{
      const response = await firstValueFrom(this.http.delete(`${this.baseUrl}/conversations/${id}/`, {
        headers: this.projectService.getAuthorizationHeader()
      }))
      this.conversations.set(await this.fetchConversations())
      if (this.activeConversation()?.id === id){
        this.initSession()
      }
      this.notification.success("Success", "Conversation deleted.");
    }
    catch(error: any){
      console.error("Error deleting conversation:", error);
      const detail =
        typeof error?.error === "string" ? "" : error?.error?.detail;
      this.notification.error("Error", "Failed to delete conversation. " + detail);
      throw error;
    }
  }

  public setActiveConvo(convo: Conversation) {
    this.isLoading.set(true)
    this.getConversation(convo.id).then((res) => {
      // only sets the messages and thread ID if they are actually recieved
      this._messages.set(res.messages);
      this._threadId.set(res.thread_id);
      // thread ID can be assigned as null. That means 2 things, if its a new chat, it hasn't yet been initialized. and if its an old chat, its not resumable. both cases, hide the input
      this.activeConversation.set(convo)
    }).finally(() => this.isLoading.set(false));
  }


  public async saveConversation(
    name: string,
    description: string,
    isPublic: boolean,
  ): Promise<Conversation | null> {
    const data = {
      name: name,
      description: description,
      public: isPublic || false,
      thread_id: this.threadId(),
      messages: this.messages(),
    };
    this.loadingConversations.set(true);
    try {
      const created = await firstValueFrom(
        this.http.post(`${this.baseUrl}/conversations/`, data, {
          headers: this.projectService.getAuthorizationHeader(),
        }),
      );
      this.fetchConversations().then((res) => {
        this.conversations.set(res);
      });
      return created as Conversation;
    } catch (error: any) {
      console.error("Error saving conversation:", error);
      const detail =
        typeof error?.error === "string" ? "" : error?.error?.detail;
      this.notification.error(
        "Error",
        "Failed to save conversation. " + detail,
      );
      return null;
    }
  }

  async updateConversation(convo: Conversation) {
    try {
      const response = await firstValueFrom(this.http.patch(`${this.baseUrl}/conversations/${convo.id}/`, {
        ...convo,
        messages: this.messages()
      }, {
        headers: this.projectService.getAuthorizationHeader()
      }))
      this.fetchConversations().then(convos => this.conversations.set(convos))
      if (this.activeConversation()?.id === convo.id) {
        this.setActiveConvo(convo);
      }
      this.notification.success("Success", "Conversation updated.");
    } catch (error: any) {
      console.error('Error updating conversation:', error)
      const detail =
        typeof error?.error === "string" ? "" : error?.error?.detail;
      this.notification.error(
        "Error",
        "Failed to update conversation. " + detail,
      );
    }
  }

  /** `fetch` bypasses Angular's HttpInterceptors, so attach the JWT ourselves. */
  private get authToken(): string {
    return (
      this.projectService.getAuthorizationHeader().get("Authorization") ?? ""
    );
  }

  /** GET /api/v2/xwai/session/ — initialize a chat session. */
  async initSession(): Promise<void> {
    this._initializing.set(true);
    try {
      const res = await firstValueFrom(
        this.http.get<SessionResponse>(`${this.baseUrl}/session/`, {
          headers: this.projectService.getAuthorizationHeader(),
        }),
      );
      this._threadId.set(res?.thread_id ?? res?.data ?? null);
      this._messages.set([
        { role: "xwai", content: { answer: "Hi! How can I help you today?" } },
      ]);
      // this._messages.set(shortExample as ChatMsg[]);
      // if the init was called for starting a new conversation, the active conversation should be set to null
      // if its the first time and/or it was already null, it'll be a no-op since its a signal and no change of state will emit
      this.activeConversation.set(null)
    } catch (err) {
      console.error("Failed to initialize XWAI v2 stream session:", err);
      this.notification.error(
        "Error",
        "Failed to initialize chat. Please refresh the page.",
      );
    } finally {
      this._initializing.set(false);
    }
  }

  /**
   * POST /api/v2/xwai/chat/stream/ — send a question and stream the reply over SSE.
   *
   * `@microsoft/fetch-event-source` handles SSE framing, POST + Bearer, and the
   * AbortController. The callbacks fire outside Angular's zone, so all view state
   * lives in signals whose `.set()/.update()` notify change detection correctly.
   */
  async sendMessage(question: string): Promise<void> {
    const trimmed = question.trim();
    if (!trimmed || this._status() === "streaming") return;

    this.appendMessage({ role: "user", content: trimmed });
    // Record for up/down recall, skipping consecutive duplicates.
    if (this.messageHistory[this.messageHistory.length - 1] !== trimmed) {
      this.messageHistory.push(trimmed);
    }
    this.startStream();
    const ctrl = this.ctrl!;

    try {
      await fetchEventSource(`${this.baseUrl}/chat/stream/`, {
        method: "POST",
        headers: {
          Authorization: this.authToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question: trimmed,
          thread_id: this._threadId(),
          job_id: this._jobId(),
        }),
        signal: ctrl.signal,
        openWhenHidden: true, // keep streaming on tab blur
        onopen: async (res) => {
          const ct = res.headers.get("content-type") ?? "";
          if (res.ok && ct.includes("text/event-stream")) return;
          const body = await res.text().catch(() => "");
          throw new Error(
            `Stream failed (${res.status}). ${body.slice(0, 300)}`.trim(),
          );
        },
        onmessage: (ev) => this.onEvent(ev.event, ev.data),
        onerror: (err) => {
          // Throw to stop the library's automatic reconnection.
          throw err;
        },
      });
    } catch (err: any) {
      if (this.isAbort(err)) return; // intentional cancel/end — already handled
      this.onStreamError(this.describeError(err));
    } finally {
      // Safety net: if the stream closed without `complete`/`end`, commit anyway.
      if (this._status() === "streaming") this.commitTurn();
    }
  }

  /** Reset the conversation back to the initial greeting. */
  reset(): void {
    this.cancel();
    this._messages.set([
      { role: "xwai", content: { answer: "Hi! How can I help you today?" } },
    ]);
  }

  /** Abort any in-flight stream (e.g. on component destroy). */
  cancel(): void {
    this.ctrl?.abort();
    this.ctrl = undefined;
    if (this._status() === "streaming") this.resetStream("idle");
  }

  // --- streaming internals ---------------------------------------------------

  private onEvent(event: string | undefined, data: string): void {
    if (!data) return;
    let d: any;
    try {
      d = JSON.parse(data);
    } catch {
      return; // ignore unparseable frames (e.g. keep-alive comments)
    }

    switch (event) {
      case "token":
        this._streamAnswer.update((a) => a + (d.text ?? ""));
        break;
      case "agent_start":
        this.pushAgent(d.agent, d.call_id, d.task);
        break;
      case "agent_token":
        this.updateAgent(d.agent, d.call_id, (a) => ({
          ...a,
          thinking: a.thinking + (d.text ?? ""),
        }));
        break;
      case "agent_step":
        this.addAgentStep(d);
        break;
      case "agent_tool_result":
        this.attachToolResult(d);
        break;
      case "agent_complete":
        this.updateAgent(d.agent, d.call_id, (a) => ({
          ...a,
          done: true,
          data: d.data,
        }));
        break;
      case "step": // orchestrator-level tool call
        this._streamSteps.update((s) => [...s, this.toStep(d)]);
        break;
      case "complete":
        if (d.answer != null) this._streamAnswer.set(d.answer);
        if (d.usage) this._streamUsage.set(d.usage);
        this._streamArtifacts.set(this.normalizeArtifacts(d.artifacts));
        this.commitTurn();
        break;
      case "error":
        this.onStreamError(d.message ?? "There was an unexpected error");
        break;
      case "end":
        if (this._status() === "streaming") this.commitTurn();
        this.ctrl?.abort();
        break;
    }
  }

  private startStream(): void {
    this.cancel();
    this._streamError.set(null);
    this.resetStream("streaming");
    this.ctrl = new AbortController();
  }

  /** Build the finalized assistant message from the streamed turn and append it. */
  private commitTurn(): void {
    if (this._status() !== "streaming") return; // guard against double-commit

    const agents = this._streamAgents();
    const orchSteps = this._streamSteps();
    const artifacts = this._streamArtifacts();
    const content: ChatResponse = {
      answer: this._streamAnswer(),
      details: this.collectDetails(),
      activity: agents.length ? agents : undefined,
      steps: orchSteps.length ? orchSteps : undefined,
      artifacts: artifacts && artifacts.length ? artifacts : undefined,
      usage: this._streamUsage() ?? undefined,
    };
    this.appendMessage({ role: "xwai", content });
    this.resetStream("done");
    this.ctrl?.abort();
  }

  private onStreamError(message: string): void {
    this._streamError.set(message);
    this.notification.error("Error", message);

    // Preserve any partial answer + structured detail + trace so nothing is lost.
    const partial = this._streamAnswer().trim();
    const details = this.collectDetails();
    const agents = this._streamAgents();
    const orchSteps = this._streamSteps();
    if (partial || details || agents.length) {
      this.appendMessage({
        role: "xwai",
        content: {
          answer: partial,
          details,
          activity: agents.length ? agents : undefined,
          steps: orchSteps.length ? orchSteps : undefined,
        },
      });
    }
    this.resetStream("error");
    this.ctrl?.abort();
  }

  private collectDetails(): AgentDetail[] | undefined {
    const details = this._streamAgents()
      .filter((a) => a.data != null)
      .map((a) => ({
        agent: a.agent,
        callId: a.callId,
        task: a.task,
        data: a.data,
      }));
    return details.length ? details : undefined;
  }

  private resetStream(status: StreamStatus): void {
    this._streamAnswer.set("");
    this._streamAgents.set([]);
    this._streamSteps.set([]);
    this._streamArtifacts.set(null);
    this._streamUsage.set(null);
    this.pendingResults.clear();
    this._status.set(status);
  }

  /** Coerce the raw `artifacts` payload to a clean array (null/non-array → null). */
  private normalizeArtifacts(raw: unknown): Artifact[] | null {
    return Array.isArray(raw) && raw.length ? (raw as Artifact[]) : null;
  }

  /** Normalize a tool event into a {@link ToolStep} (new `label`, legacy `tool`/`name`). */
  private toStep(d: any): ToolStep {
    return {
      label: d.label ?? d.tool ?? d.name ?? "tool",
      input: d.input,
      toolCallId: d.tool_call_id,
    };
  }

  /**
   * Add an `agent_step` tool call to its specialist's card. If the matching
   * `agent_tool_result` already arrived (out of order), attach its output now.
   */
  private addAgentStep(d: any): void {
    const step = this.toStep(d);
    if (step.toolCallId != null) {
      const key = this.resultKey(d.call_id, step.toolCallId);
      const buffered = this.pendingResults.get(key);
      if (buffered !== undefined) {
        step.output = buffered;
        this.pendingResults.delete(key);
      }
    }
    this.updateAgent(d.agent, d.call_id, (a) => ({
      ...a,
      steps: [...a.steps, step],
    }));
  }

  /**
   * Nest an `agent_tool_result`'s `output` under the `agent_step` it shares a
   * `tool_call_id` with, inside the same `call_id` card. If the step hasn't
   * been seen yet, buffer the output until {@link addAgentStep} drains it.
   */
  private attachToolResult(d: any): void {
    const toolCallId: string | undefined = d.tool_call_id;
    if (toolCallId == null) return;
    const output = this.stringifyOutput(d.output);

    let attached = false;
    this._streamAgents.update((list) => {
      for (let i = list.length - 1; i >= 0; i--) {
        const a = list[i];
        const matchCard =
          d.call_id != null ? a.callId === d.call_id : a.agent === d.agent;
        if (!matchCard) continue;
        const idx = a.steps.findIndex((s) => s.toolCallId === toolCallId);
        if (idx === -1) break; // right card, step not here yet — buffer below
        const steps = a.steps.slice();
        steps[idx] = { ...steps[idx], output };
        const next = list.slice();
        next[i] = { ...a, steps };
        attached = true;
        return next;
      }
      return list;
    });

    if (!attached) {
      this.pendingResults.set(this.resultKey(d.call_id, toolCallId), output);
    }
  }

  /** Stable key pairing a result with its step within one specialist invocation. */
  private resultKey(callId: string | undefined, toolCallId: string): string {
    return `${callId ?? ""} ${toolCallId}`;
  }

  /** `output` is usually a verbose serialized string; pretty-print non-strings. */
  private stringifyOutput(output: unknown): string {
    if (output == null) return "";
    if (typeof output === "string") return output;
    try {
      return JSON.stringify(output, null, 2);
    } catch {
      return String(output);
    }
  }

  private pushAgent(agent: string, callId?: string, task?: string): void {
    this._streamAgents.update((list) => [
      ...list,
      { agent, callId, task, thinking: "", steps: [], done: false },
    ]);
  }

  /**
   * Apply `fn` to the activity for this invocation. Matches on `callId` (which is
   * unique even when the same agent runs in parallel); falls back to the most
   * recent activity for `agent` when no `callId` is present. Creates one if none.
   */
  private updateAgent(
    agent: string,
    callId: string | undefined,
    fn: (a: AgentActivity) => AgentActivity,
  ): void {
    this._streamAgents.update((list) => {
      for (let i = list.length - 1; i >= 0; i--) {
        const match =
          callId != null ? list[i].callId === callId : list[i].agent === agent;
        if (match) {
          const next = list.slice();
          next[i] = fn(next[i]);
          return next;
        }
      }
      return [
        ...list,
        fn({ agent, callId, thinking: "", steps: [], done: false }),
      ];
    });
  }

  private isAbort(err: unknown): boolean {
    return (
      (err instanceof DOMException && err.name === "AbortError") ||
      (err as any)?.name === "AbortError"
    );
  }

  private describeError(err: any): string {
    return err?.message || String(err) || "There was an unexpected error";
  }

  private appendMessage(msg: ChatMsg): void {
    this._messages.update((msgs) => [...msgs, msg]);
  }
}
